import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { groupingStyles } from "../ExpenseRequest/ExpenseRequest";
import {
  CommonButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import CustomDatePickerField from "../../common/DatePicker/DatePicker";
import CustomizedSwitch from "../../common/CustomToggle/CustomToggle";
import styles from "./ViewRequests.module.scss";
import { useEffect, useState } from "react";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import apiPath from "../../../config/apiPaths.json";
import {
  apiRequest,
  catchErrorMessageHandler,
  displayDateFormat,
} from "../../../infrastructure/backendService";
import Pagination from "../../common/pagination/pagination";
import ViewExpenseRequest from "../PrintVoucher/ViewExpenseRequest/ViewExpenseRequest";
import printIcon from "../../../assets/PrintIcon.svg";
import ErrorPopup from "../../common/ErrorPopup";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import PageLoader from "../../common/PageLoader/PageLoader";

const ViewRequests = () => {
  const [isBtnLoading, setIsButtonLoading] = useState(false);
  const [expenseDetails, setExpenseDetails] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [openExpenseRequestDialog, setOpenExpenseRequestDialog] =
    useState(false);
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [viewRequestDetails, setViewRequestDetails] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const searchCriteriaList = [
    { label: "All", value: "ALL" },
    { label: "Approved", value: "APPROVED" },
    { label: "Pending", value: "PENDING" },
    { label: "Rejected", value: "REJECTED" },
    { label: "Submitted", value: "SUBMITTED" },
  ];

  const initialValues = {
    startDate: "",
    endDate: "",
    status: "ALL",
    allRequests: false,
  };

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required("From date is required"),
    endDate: Yup.string()
      .required("To date is required")
      .test(
        "is-greater",
        "To date should not be less than from date",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true; // If either date is not set, skip this validation
          return new Date(value) >= new Date(startDate);
        }
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      setIsButtonLoading(true);
      setPageNumber(1);
      getExpenseRequests(values, true);
    },
    enableReinitialize: true,
  });

  const getExpenseRequests = (values: any, isSubmit = false) => {
    setPageLoading(true);
    apiRequest({
      method: "GET",
      endpoint: apiPath.createExpenseDetails,
      includeToken: true,
      params: {
        page: isSubmit ? 0 : pageNumber - 1,
        size: 50,
        direction: "desc",
        sortBy: "dateEntered",
        startDate: displayDateFormat(new Date(values.startDate)),
        endDate: displayDateFormat(new Date(values.endDate)),
        all: values.allRequests,
        status: values.status,
      },
    })
      .then((res) => {
        setIsButtonLoading(false);
        setPageLoading(false);
        setExpenseDetails(res?.data?.content);
        setTotalRecords(res?.data?.totalElements);
        const element = document.getElementById("expense_details");
        if (element) {
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
          // Apply additional offset for a custom position
          window.scrollTo({
            top: 500,
            left: 0,
            behavior: "smooth",
          });
        }
      })
      .catch((err) => {
        setIsButtonLoading(false);
        setPageLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  useEffect(() => {
    const { startDate, endDate } = formik.values;
    if (startDate && endDate) {
      getExpenseRequests(formik.values);
    }
  }, [pageNumber]);

  const handleChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const handlePrint = (referenceNum: any) => {
    setOpenExpenseRequestDialog(true);
    setReferenceNumber(referenceNum);
  };

  const handleView = (referenceNum: any) => {
    setOpenExpenseRequestDialog(true);
    setViewRequestDetails(true);
    setReferenceNumber(referenceNum);
  };

  const renderFieldsComponent = () => (
    <Box className="common_box_shadow" sx={{ mt: "20px" }}>
      <Box sx={groupingStyles.groupedFieldsContainer}>
        <Box sx={groupingStyles.fieldContainer}>
          {CustomRequiredInputLabel("From", true)}
          <CustomDatePickerField
            date={formik.values.startDate}
            handleDateChange={handleChange}
            errorMessage={
              formik.touched.startDate ? formik.errors.startDate : ""
            }
            name="startDate"
          />
        </Box>
        <Box sx={groupingStyles.fieldContainer}>
          {CustomRequiredInputLabel("To", true)}
          <CustomDatePickerField
            date={formik.values.endDate}
            handleDateChange={handleChange}
            errorMessage={formik.touched.endDate ? formik.errors.endDate : ""}
            name="endDate"
          />
        </Box>
      </Box>
      <Box sx={groupingStyles.groupedFieldsContainer}>
        <Box sx={groupingStyles.fieldContainer}>
          {CustomRequiredInputLabel("Search Criteria", true)}
          <CustomSelectField
            handleDropdownChange={handleChange}
            errorMessage={""}
            dropDownList={searchCriteriaList}
            value={formik.values.status}
            fieldName="status"
            placeholder="Select search criteria"
            optionLabel="label"
            optionValue="value"
          />
        </Box>
        <Box
          sx={{
            ...groupingStyles.fieldContainer,
            display: "flex",
            alignItems: "center",
            height: "48px",
            mt: "22px",
          }}
        >
          <Typography className={styles.toggle_text} mr="13px">
            My Requests
          </Typography>
          <CustomizedSwitch
            fieldName={"allRequests"}
            isChecked={formik.values.allRequests}
            handleChange={handleChange}
          />
          <Typography className={styles.toggle_text} ml="13px">
            View All Requests
          </Typography>
        </Box>
      </Box>
      {renderButtonComponent()}
    </Box>
  );

  const renderButtonComponent = () => {
    return (
      <Box>
        <CommonButton
          onClick={() => {
            !isBtnLoading && formik.handleSubmit();
          }}
        >
          <ButtonLoader isBtnLoading={isBtnLoading} btnText={"Submit"} />
        </CommonButton>
      </Box>
    );
  };

  const renderEVoucherRequestsTable = () => {
    return (
      <Box mt="25px" className={styles.expense_request_details_container}>
        {expenseDetails?.length > 0 ? (
          <>
            <Box
              sx={{ mt: "15px", width: "100%", overflowX: "auto" }}
              id="expense_details"
            >
              <table className={styles.table_container}>
                <thead>
                  <th>Sr No</th>
                  <th>Submitted on</th>
                  <th>Status</th>
                  <th>
                    Short Title <br />
                    (Reference Number)
                  </th>
                  <th>Amount</th>
                  <th className="display_none_ipad">Expense Details</th>
                  <th>Action</th>
                </thead>
                <tbody>
                  {expenseDetails?.map?.((row: any, index: number) => (
                    <tr key={index}>
                      <td>{index + 1 + (pageNumber - 1) * 50}</td>
                      <td>{row?.dateEntered}</td>
                      <td>{row?.status}</td>
                      <td
                        className={styles.table_desc_txt}
                        onClick={() => handleView(row?.referenceNumber)}
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {row?.shortTitle} ({row?.referenceNumber})
                      </td>
                      <td className={styles.amount_text}>
                        ${row?.amount?.toFixed(2)}
                      </td>
                      <td
                        className={`${styles.table_desc_txt} display_none_ipad`}
                      >
                        {row?.expenseDetails}
                      </td>
                      <td>
                        <Box sx={{ textAlign: "center" }}>
                          <Box
                            component={"img"}
                            src={printIcon}
                            alt="Print"
                            onClick={() => handlePrint(row?.referenceNumber)}
                            sx={{ cursor: "pointer" }}
                          />
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {totalRecords > 0 && (
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", mt: "15px" }}
              >
                <Pagination
                  items={totalRecords}
                  itemsPerPage={50}
                  setPageNumber={setPageNumber}
                  pageNumber={pageNumber}
                />
              </Box>
            )}
          </>
        ) : (
          <Typography
            sx={{ mt: "15px" }}
            className={styles.expense_request_txt}
          >
            No requests found.
          </Typography>
        )}
      </Box>
    );
  };

  const renderViewRequests = () => {
    return (
      <>
        <Typography className="sub_heading">View Request</Typography>
        {renderFieldsComponent()}
        {expenseDetails && renderEVoucherRequestsTable()}
        {openExpenseRequestDialog && (
          <ViewExpenseRequest
            open={openExpenseRequestDialog}
            handleClose={() => {
              setOpenExpenseRequestDialog(false);
              setViewRequestDetails(false);
            }}
            referenceNumber={referenceNumber}
            viewRequest={viewRequestDetails}
          />
        )}
        <PageLoader loading={pageLoading} />
      </>
    );
  };

  return (
    <Box className={styles.view_requests_main}>{renderViewRequests()}</Box>
  );
};

export default ViewRequests;
